import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';

import {
  bool, func, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Dropdown from '../../../dropdown';
import { createProductType, listProductTypes } from '../../../../store/productTypes/actions';
import localization from '../../../../shared/strings';
import Toast from '../../../Toast/index';
import {
  filterProductTypesByQuery,
  findProductTypeByNormalizedTitle,
  getCheckedProductTypeItems,
  shouldApplyProductTypeCreateResult,
} from './helpers';

const l10n = localization.PRODUCT_INFO.productType;

const ProductTypesDropdown = ({
  value,
  disabled,
  onSelect,
  onClear,
}) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.productTypes.items);
  const isLoading = useSelector((state) => state.productTypes.isLoading);
  const isLoaded = useSelector((state) => state.productTypes.isLoaded);
  /**
   * Guards against double-applying a freshly created title: the generic Dropdown component
   * auto-fires onCheckedHandler (as an array) once the newly created item lands in `items`,
   * independently of the manual apply below. Whichever path runs first "claims" the pending
   * creation so the other one becomes a no-op.
   */
  const isPendingCreateAppliedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded && !isLoading) dispatch(listProductTypes());
  }, [dispatch, isLoaded, isLoading]);

  const checkedItems = useMemo(
    () => getCheckedProductTypeItems(items, value),
    [items, value],
  );

  const handleSelect = useCallback((selectedProductTypeOrList) => {
    /** Dropdown may call back with an array when it auto-selects a freshly created item */
    const isAutoSelectAfterCreate = Array.isArray(selectedProductTypeOrList);
    const selectedProductType = isAutoSelectAfterCreate
      ? selectedProductTypeOrList[0]
      : selectedProductTypeOrList;
    if (!selectedProductType) return;
    if (isAutoSelectAfterCreate) {
      if (isPendingCreateAppliedRef.current) return;
      isPendingCreateAppliedRef.current = true;
    }
    onSelect(selectedProductType);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    onClear?.();
  }, [onClear]);

  const handleCreate = useCallback(async (query) => {
    const trimmedTitle = query.trim();
    if (!trimmedTitle) return;

    const existingProductType = findProductTypeByNormalizedTitle(items, trimmedTitle);
    if (existingProductType) {
      onSelect(existingProductType);
      return;
    }

    isPendingCreateAppliedRef.current = false;
    const result = await dispatch(createProductType({ title: trimmedTitle }));
    /** Dropdown's own auto-select already applied the freshly created item, nothing to do */
    if (isPendingCreateAppliedRef.current) return;
    /** genuine failure (network/5xx/permission): do not apply an unsaved title */
    if (!shouldApplyProductTypeCreateResult(result)) {
      Toast(l10n.createFailed, { autoClose: false, type: 'error' });
      return;
    }
    isPendingCreateAppliedRef.current = true;
    const productTypeToApply = result.payload?.productType
      || findProductTypeByNormalizedTitle(items, trimmedTitle)
      || { title: trimmedTitle };
    onSelect(productTypeToApply);
  }, [dispatch, items, onSelect]);

  const filterFunc = useCallback(
    (query) => filterProductTypesByQuery(items, query),
    [items],
  );

  const placeholder = isLoaded ? l10n.noProductTypesFound : l10n.loadingProductTypes;

  return (
    <Dropdown
      items={items}
      checkedItems={checkedItems}
      type="keyword"
      showChips
      showDeletedOptions
      showAddButton={false}
      disabled={disabled}
      isOnlyCreate
      filterText={l10n.placeholder}
      placeholder={placeholder}
      createPlaceholderText={l10n.noProductTypesFoundCreate}
      createHandler={handleCreate}
      isItemsLoaded={isLoaded}
      inProgress={isLoading}
      filterFunc={filterFunc}
      additionalClass="productTypeDropdown"
      closeOnSelect
      tooltipTextClose={l10n.clearTooltip}
      onCheckedHandler={handleSelect}
      onUncheckedHandler={handleClear}
    />
  );
};

ProductTypesDropdown.propTypes = {
  value: string,
  disabled: bool,
  onSelect: func.isRequired,
  onClear: func,
};

ProductTypesDropdown.defaultProps = {
  value: '',
  disabled: false,
  onClear: null,
};

export default ProductTypesDropdown;
